import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useChatWebSocket, chatWebSocketService } from './useChatWebSocket';
import { useInitializeChatSessionMutation, useSendMessageMutation } from './useChatSupportMutations';
import { useChatMessagesQuery } from './useChatSupportQueries';
import { userInfoSchema, messageSchema } from '../validationSchemas';
import { convertApiMessagesToChatMessages, generateUserMessageId, formatMessageText } from '../utils/messageUtils';

export const useChatSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isUserInfoSubmitted, setIsUserInfoSubmitted] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [shouldRefetchMessages, setShouldRefetchMessages] = useState(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const typingTimeoutRef = useRef(null);
  const connectingRef = useRef(false);
  const connectedSessionIdRef = useRef(null);
  const connectWebSocketRef = useRef(null);

  // Global WebSocket context
  const {
    connectionState: wsConnectionState,
    isConnected,
    messages,
    unreadCount,
    hasNewMessages,
    isTyping,
    addMessage,
    clearMessages,
    markMessagesAsRead,
    resetNotificationState,
    syncUnreadFromHistory,
    sendTyping,
    connect: connectWebSocket,
    setChatOpen,
    sessionCleared,
    resetSessionClearedFlag
  } = useChatWebSocket();

  // Keep ref updated with latest connectWebSocket function
  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  // Form methods for user info
  const userInfoMethods = useForm({
    resolver: yupResolver(userInfoSchema),
    defaultValues: {
      customer_name: '',
      customer_email: '',
      // channel_id: process.env.REACT_APP_MATTERMOST_CHANNEL_ID || '',
    },
  });

  // Form methods for messages
  const messageMethods = useForm({
    resolver: yupResolver(messageSchema),
    defaultValues: {
      message: '',
    },
  });

  // Mutation hooks
  const initializeChatSessionMutation = useInitializeChatSessionMutation();
  const sendMessageMutation = useSendMessageMutation();

  // Query hook for fetching old messages
  const chatMessagesQuery = useChatMessagesQuery(
    currentSession?.session_id || null,
    (isUserInfoSubmitted && !!currentSession) || shouldRefetchMessages
  );

  // Update chat open state when isOpen changes and register close function
  useEffect(() => {
    setChatOpen(isOpen, () => {
      setIsOpen(false);
      setIsMinimized(false);
    });
  }, [isOpen, setChatOpen]);

  // Listen for session cleared flag and reset all local state
  useEffect(() => {
    if (sessionCleared) {
      console.log('🔌 Session cleared detected, resetting ChatSupport local state');
      
      // Reset all local state
      setCurrentSession(null);
      setIsUserInfoSubmitted(false);
      setIsInitializing(false);
      setIsOpen(false);
      setIsMinimized(false);
      setShowScrollToBottom(false);
      setShouldRefetchMessages(false);
      setIsSubmittingMessage(false);
      
      // Reset connection refs
      connectingRef.current = false;
      connectedSessionIdRef.current = null;
      
      // Reset form states
      userInfoMethods.reset();
      messageMethods.reset();
      
      // Clear any timeouts
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      console.log('🔌 ChatSupport local state reset complete');
    }
  }, [sessionCleared, userInfoMethods, messageMethods]);

  // Restore existing chat session on page reload so WS can reconnect in background
  useEffect(() => {
    const existingSessionId = localStorage.getItem('chat_session_id');
    const existingSessionData = localStorage.getItem('chat_session_data');

    if (!existingSessionId || !existingSessionData) {
      return;
    }

    try {
      const parsedSession = JSON.parse(existingSessionData);
      const restoredSession = {
        ...parsedSession,
        session_id: parsedSession?.session_id || existingSessionId,
      };

      setCurrentSession(prevSession => {
        if (prevSession?.session_id === restoredSession.session_id) {
          return prevSession;
        }
        return restoredSession;
      });
      setIsUserInfoSubmitted(true);
      setShouldRefetchMessages(true);

      // Allow reconnection flow for restored session
      connectingRef.current = false;
      connectedSessionIdRef.current = null;
    } catch (error) {
      console.error('Failed to restore chat session from localStorage:', error);
      localStorage.removeItem('chat_session_id');
      localStorage.removeItem('chat_session_data');
    }
  }, []);

  // Check if localStorage session was cleared but state still has session
  useEffect(() => {
    if (currentSession && isUserInfoSubmitted) {
      const localStorageSessionId = localStorage.getItem('chat_session_id');
      const localStorageSessionData = localStorage.getItem('chat_session_data');
      
      // If localStorage is cleared but we still have session in state, reset it
      if (!localStorageSessionId || !localStorageSessionData) {
        console.log('🔍 localStorage session cleared but state still has session, resetting');
        setCurrentSession(null);
        setIsUserInfoSubmitted(false);
        setIsInitializing(false);
        clearMessages();
        userInfoMethods.reset();
        messageMethods.reset();
        connectingRef.current = false;
        connectedSessionIdRef.current = null;
      }
    }
  }, [currentSession, isUserInfoSubmitted, clearMessages, userInfoMethods]);

  // Handle query errors when chat is open - ensure form shows immediately
  useEffect(() => {
    if (chatMessagesQuery.isError && isOpen && currentSession) {
      console.log('🔍 Query error detected while chat is open, resetting session immediately');
      setCurrentSession(null);
      setIsUserInfoSubmitted(false);
      setIsInitializing(false);
      clearMessages();
      setShouldRefetchMessages(false);
      userInfoMethods.reset();
      messageMethods.reset();
      connectingRef.current = false;
      connectedSessionIdRef.current = null;
    }
  }, [chatMessagesQuery.isError, isOpen, currentSession, clearMessages, userInfoMethods]);

  // Handle old messages when they are fetched
  useEffect(() => {
    if (chatMessagesQuery?.data && chatMessagesQuery.data?.messages && currentSession) {
      
      // Only clear existing messages if we're refetching (not on initial load)
      if (shouldRefetchMessages) {
        clearMessages();
      }
      
      // Remove first element if messages exist and have elements
      const messagesToProcess =
        chatMessagesQuery.data?.messages.length > 0 
        ? chatMessagesQuery.data?.messages.slice(1) 
        : chatMessagesQuery.data?.messages || [];

      // Convert API messages to ChatMessage format
      const oldMessages = convertApiMessagesToChatMessages(
        messagesToProcess,
        currentSession.session_id
      );
      
      // Add messages to global context with deduplication
      oldMessages.forEach(message => {
        // Check if message already exists to avoid duplicates
        const messageExists = messages.some(msg => 
          msg.id === message.id
        );
        
        if (!messageExists) {
          addMessage(message);
        }
      });

      // Recalculate unread badge for restored sessions when chat is closed
      syncUnreadFromHistory(currentSession.session_id, oldMessages);
      
      
      // Reset refetch flag
      setShouldRefetchMessages(false);
      
      // WebSocket connection will be handled by separate useEffect
    } else if (chatMessagesQuery.isError && currentSession) {
      // If error occurs, reset session and show form page
      console.log('🔍 Error fetching messages, resetting session and showing form');
      
      // Clear session data
      setCurrentSession(null);
      setIsUserInfoSubmitted(false);
      setIsInitializing(false);
      clearMessages();
      
      // Reset refetch flag
      setShouldRefetchMessages(false);
      
      // Reset form states
      userInfoMethods.reset();
      messageMethods.reset();
      
      // Reset connection refs
      connectingRef.current = false;
      connectedSessionIdRef.current = null;
    }
  }, [chatMessagesQuery.isSuccess, chatMessagesQuery.isError, chatMessagesQuery.data, currentSession?.session_id, shouldRefetchMessages, clearMessages, addMessage, syncUnreadFromHistory, userInfoMethods, messageMethods]);

  // Separate useEffect for WebSocket connection to prevent infinite loops
  useEffect(() => {
    const sessionId = currentSession?.session_id;
    
    // Guard conditions to prevent infinite loops
    if (!sessionId || !isUserInfoSubmitted) {
      return;
    }

    // If already connected to this session, don't reconnect
    if (isConnected && connectedSessionIdRef.current === sessionId) {
      return;
    }

    // Prevent re-connection if already connecting to this session
    if (connectingRef.current && connectedSessionIdRef.current === sessionId) {
      return;
    }

    // Connect immediately when session is available (including restored sessions on reload)
    if (!isConnected) {
      const connectWebSocketForSession = async () => {
        // Set connecting flag to prevent multiple simultaneous connection attempts
        if (connectingRef.current) {
          console.log('🔌 Already connecting, skipping...');
          return;
        }
        
        connectingRef.current = true;
        connectedSessionIdRef.current = sessionId;
        
        console.log('🔌 Connecting WebSocket for session:', sessionId);
        try {
          const testResult = await chatWebSocketService.testConnection(sessionId);
          if (testResult.success) {
            // Use ref to avoid dependency issues
            if (connectWebSocketRef.current) {
              await connectWebSocketRef.current(sessionId);
              console.log('🔌 WebSocket connected successfully');
              // Keep connectedSessionIdRef set to track successful connection
            }
          } else {
            console.warn('🔌 WebSocket connection test failed:', testResult.error);
            console.log('🔌 Continuing with REST API fallback');
            // Reset flags on failure after a delay
            setTimeout(() => {
              connectingRef.current = false;
              if (connectedSessionIdRef.current === sessionId) {
                connectedSessionIdRef.current = null;
              }
            }, 1000);
          }
        } catch (error) {
          console.error('🔌 Failed to connect WebSocket:', error);
          console.log('🔌 Continuing with REST API fallback');
          // Reset flags on error after a delay
          setTimeout(() => {
            connectingRef.current = false;
            if (connectedSessionIdRef.current === sessionId) {
              connectedSessionIdRef.current = null;
            }
          }, 1000);
        }
      };

      connectWebSocketForSession();
    }
  }, [currentSession?.session_id, isUserInfoSubmitted, isConnected]);

  const handleToggleChat = async () => {
    if (!isOpen) {
      // Mark messages as read when opening chat
      markMessagesAsRead(currentSession?.session_id || localStorage.getItem('chat_session_id'));
      
      // Check if we already have a session in localStorage
      const existingSessionId = localStorage.getItem('chat_session_id');
      const existingSessionData = localStorage.getItem('chat_session_data');
      
      if (existingSessionId && existingSessionData) {
        // Reuse existing session
        console.log('🔌 Found existing session, reusing:', existingSessionId);
        try {
          const sessionData = JSON.parse(existingSessionData);
          setCurrentSession(sessionData);
          setIsUserInfoSubmitted(true);
          
          // Reset connection refs for new session
          connectingRef.current = false;
          connectedSessionIdRef.current = null;
          
          // Connect WebSocket if not already connected
          if (!isConnected && connectWebSocketRef.current) {
            await connectWebSocketRef.current(existingSessionId);
          }
          
          // If we have no messages in the global context, reload them from the API
          if (messages.length === 0) {
            console.log('🔌 No messages in context, triggering message reload');
            setShouldRefetchMessages(true);
          }
        } catch (error) {
          console.error('Failed to parse existing session data:', error);
          // Fallback to creating new session
          await createNewSession();
        }
      } else {
        // No existing session, create new one
        await createNewSession();
      }
    }
    
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const createNewSession = async () => {
    // Reset session cleared flag for new session
    resetSessionClearedFlag();
    
    // Reset connection refs for new session
    connectingRef.current = false;
    connectedSessionIdRef.current = null;
    
    // Check if user data exists in localStorage
    const userName = localStorage.getItem('user_name');
    const userEmail = localStorage.getItem('email');
    
    if (userName && userEmail) {
      // User data exists, directly initialize chat session
      console.log('🔌 User data found in localStorage, initializing chat session directly');
      setIsInitializing(true);
      
      try {
        const userInfo = {
          customer_name: userName,
          customer_email: userEmail,
          // channel_id: process.env.REACT_APP_MATTERMOST_CHANNEL_ID || ''
        };
        
        const response = await initializeChatSessionMutation.mutateAsync(userInfo);
        if (response.success && response.data) {
          setCurrentSession(response.data);
          setIsUserInfoSubmitted(true);
        }
      } catch (error) {
        console.error('Failed to initialize chat session:', error);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleUserInfoSubmit = async (data) => {
    try {
      // Reset session cleared flag for new session
      resetSessionClearedFlag();
      
      const response = await initializeChatSessionMutation.mutateAsync({
        ...data,
        // chat_type: 'Chat Support'
        chat_type: 'Chat Support'

      });
      if (response.success && response.data) {
        setCurrentSession(response.data);
        setIsUserInfoSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to initialize chat session:', error);
    }
  };

  const handleSendMessage = async (data) => {
    if (!currentSession || isSubmittingMessage) {
      console.log('🔌 Message submission blocked - no session or already submitting');
      return;
    }
    
    // Prevent multiple submissions
    setIsSubmittingMessage(true);
    
    try {
      console.log('🔌 Sending message via REST API');
      
      // Generate a unique message ID that will be consistent across REST and WebSocket
      const messageId = generateUserMessageId(currentSession.session_id);
      
      // Add user message immediately for better UX with consistent ID
      const userMessage = {
        id: messageId,
        sessionId: currentSession.session_id,
        message: formatMessageText(data.message), // Apply formatting to user messages
        sender: 'user',
        timestamp: new Date().toISOString(),
        isRead: true
      };
      addMessage(userMessage);
      
      // Send message via REST API
      const response = await sendMessageMutation.mutateAsync({
        sessionId: currentSession.session_id,
        messageData: data
      });
      
      if (response.success && response.data) {
        // Only add support response if it's not already in messages (avoid duplicates)
        if (response.data.supportResponse) {
          const supportMessage = response.data.supportResponse;
          // Check if this support message already exists to avoid duplicates
          const messageExists = messages.some(msg => 
            msg.id === supportMessage.id || 
            (msg.message === supportMessage.message && 
             msg.sender === supportMessage.sender && 
             Math.abs(new Date(msg.timestamp).getTime() - new Date(supportMessage.timestamp).getTime()) < 1000)
          );
          
          if (!messageExists) {
            addMessage(supportMessage);
          }
        }
      }
      
      // Reset form only after successful submission
      messageMethods.reset();
    } catch (error) {
      console.error('Failed to send message:', error);
      // If sending fails, we might want to remove the optimistically added message
      // But for now, we'll keep it as the user did type the message
    } finally {
      // Always reset the submission state
      setIsSubmittingMessage(false);
    }
  };

  const handleCloseChat = async () => {
    if (currentSession) {
      try {
        // Reset local state but keep session data in localStorage for reuse
        setCurrentSession(null);
        setIsUserInfoSubmitted(false);
        userInfoMethods.reset();
        messageMethods.reset();
        
        // Don't clear messages when closing chat - they should persist for session reuse
        // Only reset notification state to clear unread indicators
        resetNotificationState(currentSession?.session_id);
      } catch (error) {
        console.error('Failed to close chat session:', error);
      }
    }
    setIsOpen(false);
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    // This will be handled by ChatMessages component
    setShowScrollToBottom(false);
  };

  // Handle scroll detection to show/hide scroll-to-bottom button
  const handleScroll = () => {
    setShowScrollToBottom(true);
  };

  // Handle typing indicator
  const handleTyping = (message) => {
    if (isConnected && message.length > 0) {
      sendTyping(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(false);
      }, 1000);
    }
  };

  // Handle key press in message input
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Enter key without Shift - send message
      event.preventDefault();
      
      // Prevent submission if already submitting
      if (isSubmittingMessage) {
        console.log('🔌 Key press submission blocked - already submitting');
        return;
      }
      
      const messageValue = messageMethods.getValues('message');
      if (messageValue.trim()) {
        handleSendMessage({ message: messageValue });
      }
    }
    // Shift + Enter allows new line (default behavior)
  };

  return {
    // State
    isOpen,
    isMinimized,
    isUserInfoSubmitted,
    currentSession,
    isInitializing,
    showScrollToBottom,
    isSubmittingMessage,
    messages,
    unreadCount,
    hasNewMessages,
    isTyping,
    connectionState: wsConnectionState,
    
    // Form methods
    userInfoMethods,
    messageMethods,
    
    // Mutations
    initializeChatSessionMutation,
    sendMessageMutation,
    
    // Handlers
    handleToggleChat,
    handleMinimize,
    handleUserInfoSubmit,
    handleSendMessage,
    handleCloseChat,
    handleScroll,
    scrollToBottom,
    handleKeyPress,
  };
};
